import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteGameServer, getPoolInfo, listGameServerRooms, getGameServerLogs, getGameServerMatch, listGameServerMatches, listGameServers } from './api';
import { GameServerState } from './types';

export function useDeleteGameServer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteGameServer,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['game-servers'] });
      queryClient.invalidateQueries({ queryKey: ['game-server-pool-config'] });
      queryClient.invalidateQueries({ queryKey: ['game-server-rooms'] });
    },
  });
}

export function useGameServers(page: number, perPage: number, status?: GameServerState, id?: string, autoRefresh = false) {
  return useQuery({
    queryKey: ['game-servers', page, perPage, status, id],
    queryFn: () => listGameServers(page, perPage, status, id),
    placeholderData: keepPreviousData,
    refetchInterval: autoRefresh ? 5000 : false,
  });
}

export function useGameServerLogs(serverID?: string) {
  return useQuery({
    queryKey: ['game-server-logs', serverID],
    queryFn: () => getGameServerLogs(serverID!),
    enabled: Boolean(serverID),
  });
}

export function useGameServerMatches(serverID: string, page: number) {
  return useQuery({
    queryKey: ['game-server-matches', serverID, page],
    queryFn: () => listGameServerMatches(serverID, page),
    placeholderData: keepPreviousData,
  });
}

export function useGameServerMatchDetail(
  serverID?: string,
  roomID?: number,
  matchNumber?: number
) {
  return useQuery({
    queryKey: ['game-server-match', serverID, roomID, matchNumber],
    queryFn: () => getGameServerMatch(serverID!, roomID!, matchNumber!),
    enabled: Boolean(serverID && roomID && matchNumber),
  });
}

export function useGameServerRooms(serverID: string, page: number, autoRefresh: boolean) {
  return useQuery({
    queryKey: ['game-server-rooms', serverID, page],
    queryFn: () => listGameServerRooms(serverID, page),
    refetchInterval: autoRefresh ? 5000 : false,
  });
}

export function usePoolInfo(autoRefresh: boolean) {
  return useQuery({
    queryKey: ['game-server-pool-config'],
    queryFn: getPoolInfo,
    refetchInterval: autoRefresh ? 5000 : false,
  });
}
